<template>
  <div class="main-card h-full">
    <div class="w-11 flex flex-column">
      <div
        class="flex flex-column gap-2"
        style="flex-direction: row !important; justify-content: space-between; align-items: center"
      >
        <div class="left">
          <h1 class="title">Nueva actuación</h1>
          <h2 class="flow-subtitle">{{ flowLabel }}</h2>
        </div>
        <div class="right">
          <Button class="deleteButton" @click="openDeleteModal" :disabled="!wizard.isDirty"
            >Borrar datos</Button
          >
        </div>
      </div>

      <div class="w-full flex justify-content-center">
        <Steps
          ref="stepsRef"
          :model="items"
          :activeIndex="activeIndex"
          :readonly="false"
          :exact="true"
          class="mt-6 w-9 steps-readonly"
        />
      </div>

      <div class="w-full flex justify-content-between align-items-center mt-2">
        <h2 class="stepName">{{ currentStepLabel }}</h2>
        <div class="flex gap-3">
          <Button
            v-if="isMultipleLoading && currentStore.isView && currentStore.selectedId"
            class="deleteButton"
            :disabled="currentStore.isLoading"
            @click="showConfirmDelete"
          >
            <div>
              <Trash style="width: 20px; height: 20px" />
            </div>
          </Button>
          <Button
            v-if="isMultipleLoading && currentStore.selectedId && currentStore.isView"
            class="button primaryButton"
            :disabled="currentStore.isLoading"
            @click="currentStore.startEdit()"
          >
            <div class="flex align-items-center gap-2 justify-content-center w-full">
              <Pencil style="width: 20px; height: 20px" />
              <span>Editar</span>
            </div>
          </Button>
          <Button
            v-if="!changeLabelBtn"
            class="secondaryButton button"
            @click="back"
            :disabled="activeIndex === 0"
          >
            <div class="flex align-items-center w-full gap-2 justify-content-start">
              <ArrowLeft style="width: 20px; height: 20px" />
              <span class="ml-2">Atrás</span>
            </div>
          </Button>
          <Button v-else class="secondaryButton button" @click="showCancelModal()">
            <div class="flex align-items-center w-full gap-2 justify-content-start">
              <X style="width: 20px; height: 20px" />
              <span class="ml-2">Cancelar</span>
            </div>
          </Button>
          <Button
            v-if="!changeLabelBtn && !isLastStep"
            class="button primaryButton"
            @click="next"
            :disabled="!isCurrentStepValid"
          >
            <div class="flex align-items-center gap-2 justify-content-center">
              <span>Siguiente</span>
              <ArrowRight style="width: 20px; height: 20px" />
            </div>
          </Button>

          <Button
            v-if="!changeLabelBtn && isLastStep"
            class="button primaryButton"
            @click="handleSaveActuacion"
            :disabled="wizard.submitting"
          >
            <div
              v-if="!wizard.submitting"
              class="flex align-items-center gap-2 justify-content-center"
            >
              <Check style="width: 20px; height: 20px" />
              <span>Guardar</span>
            </div>
            <div v-else class="flex align-items-center gap-2 justify-content-center">
              <i class="pi pi-spin pi-spinner mr-2" style="font-size: 1rem" />
              <span>Guardando...</span>
            </div>
          </Button>

          <Button
            v-if="changeLabelBtn"
            class="button primaryButton"
            @click="saveData"
            :disabled="currentStore.isLoading || !currentStore.isFormValid"
          >
            <div
              v-if="!currentStore.isLoading"
              class="flex align-items-center gap-2 justify-content-center"
            >
              <Check style="width: 20px; height: 20px" />
              <span>Guardar</span>
            </div>
            <div v-else class="flex align-items-center gap-2 justify-content-center">
              <i class="pi pi-spin pi-spinner mr-2" style="font-size: 1rem" />
              <span>Guardando...</span>
            </div>
          </Button>
        </div>
      </div>

      <div class="step-content">
        <RouterView v-slot="{ Component }">
          <component
            :is="Component"
            :key="route.fullPath"
            ref="activeStepRef"
            :actuacion-id="actuacionId"
            :initial-mode="actuacionId ? 'edit' : 'create'"
          />
        </RouterView>
      </div>

      <CustomModal
        ref="deleteModal"
        modalType="delete"
        :showIcon="true"
        title="¿Eliminar actuación?"
        message="Se eliminarán todos los datos cargados hasta el momento. Esta acción no se puede deshacer."
        :buttonLabels="['Sí, eliminar', 'Cancelar']"
        @confirm="onConfirmDelete"
        @cancel="onCancelDelete"
        @close="onCancelDelete"
      />
      <CustomModal
        ref="confirmationModalRef"
        :title="modalConfig.title"
        :message="modalConfig.message"
        :modalType="modalConfig.modalType"
        :showSecondaryButton="modalConfig.showSecondaryButton"
        :button-labels="modalConfig.buttonLabels"
        :closable="modalConfig.closable ?? true"
        @confirm="modalConfig.onConfirm"
        @cancel="modalConfig.onCancel"
      >
        <template v-if="createdRedirectCountdown !== null" #footer>
          <div class="w-full flex flex-column align-items-center pt-2">
            <Button
              :label="modalConfig.buttonLabels?.[0] || 'Aceptar'"
              class="created-redirect-button"
              :style="createdSuccessButtonStyle"
              @click="modalConfig.onConfirm"
            />
            <small v-if="createdRedirectCountdown !== null" class="modal-footer-note primaryFont">
              Redirigiendo en {{ createdRedirectCountdown }}
            </small>
          </div>
        </template>
      </CustomModal>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouterCompat } from "@/composables/useRouterCompat";
import { getStepDefs, getFlowLabel } from "@/pages/newActuacion/stepsConfig";
import { ArrowRight, ArrowLeft, Trash, X, Check, Pencil } from "lucide-vue";
import { useActuacionWizardStore } from "@/store/Pinia/useActuacionWizardStore";
import { useLegalDataStore } from "@/store/Pinia/useLegalDataStore";
import { useVinculatedStore } from "@/store/Pinia/useVinculatedStore";
import { useAffectedStore } from "@/store/Pinia/useAffectedStore";
import { useEffectStore } from "@/store/Pinia/useEffectStore";
import { usePersonalIntervinienteStore } from "@/store/Pinia/usePersonalIntervinienteStore";
import { useAfectacionStore } from "@/store/Pinia/useAfectacionStore";
import { useCatalogs } from "@/composables/useCatalogs";
import CustomModal from "@/components/CustomModal.vue";
import { onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router/composables";
import { useActuacionSubmit } from "@/composables/useActuacionSubmit";

const { route, router } = useRouterCompat();
const { preloadCatalogs } = useCatalogs();
const wizard = useActuacionWizardStore();
const legalStore = useLegalDataStore();
const vinculatedStore = useVinculatedStore();
const affectedStore = useAffectedStore();
const effectStore = useEffectStore();
const intervinienteStore = usePersonalIntervinienteStore();
const afectacionStore = useAfectacionStore();

const deleteModal = ref(null);
const confirmationModalRef = ref(null);
const activeStepRef = ref(null);
const createdRedirectCountdown = ref(null);

let createdRedirectTimeout = null;
let createdRedirectInterval = null;

// Bandera que evita que el leave-guard muestre el modal de "cancelar carga"
// cuando navegamos por nuestra propia voluntad tras un submit exitoso.
const skipLeaveGuard = ref(false);

const { submit } = useActuacionSubmit();

const flowKey = computed(() => route.query?.type || "sumario-denuncia");
const stepDefs = computed(() => getStepDefs(flowKey.value) || []);
const flowLabel = computed(() => getFlowLabel(flowKey.value));
const stepsRef = ref(null);
const createdSuccessButtonStyle = {
  backgroundColor: "#268C2D",
  borderColor: "#268C2D",
  color: "#FFFFFF",
};

const storesMap = {
  Afectados: {
    label: "afectado",
    store: affectedStore,
    delete: affectedStore.handleDeleteAffected,
  },
  Vinculados: {
    label: "vinculado",
    store: vinculatedStore,
    delete: vinculatedStore.handleDeleteVinculated,
  },
  Efectos: {
    label: "efecto",
    store: effectStore,
    delete: effectStore.handleDeleteEffect,
  },
  "Personal Interviniente": {
    label: "interviniente",
    store: intervinienteStore,
    delete: intervinienteStore.handleDeleteInterviniente,
  },
  "Afectación Patrimonial": {
    label: "afectacion",
    store: afectacionStore,
    delete: afectacionStore.handleDeleteAfectacion,
  },
};

const currentStepConfig = computed(() => {
  const stepName = currentStepLabel.value;
  return storesMap[stepName] || null;
});

const currentStore = computed(() => currentStepConfig.value?.store);

const isMultipleLoading = computed(() => !!currentStepConfig.value);

const changeLabelBtn = computed(() => {
  const store = currentStore.value;
  const isStepCreateOrEdit = store ? store.isCreate || store.isEdit : false;

  return isMultipleLoading.value && isStepCreateOrEdit;
});

const stepNameToWizardKey = {
  "newActuacion-afectados": "afectados",
  "newActuacion-vinculados": "vinculados",
  "newActuacion-fecha": "fecha",
  "newActuacion-efectos": "efectos",
  "newActuacion-personal-interviniente": "intervinientes",
  "newActuacion-afectacion-patrimonial": "afectaciones",
  "newActuacion-datos": "datosLegales",
  "newActuacion-visualizacion": "visualizacion",
};

const items = computed(() =>
  stepDefs.value.map((s) => {
    const wizardKey = stepNameToWizardKey[s.name];
    const hasError = wizardKey && wizard.stepHasError(wizardKey);
    return {
      label: s.label,
      to: `${s.path}?type=${flowKey.value}`,
      class: hasError ? "wizard-step-error" : undefined,
    };
  })
);

const activeIndex = computed(() => {
  const currentName = route.name;
  const idx = stepDefs.value.findIndex((s) => s.name === currentName);
  return idx >= 0 ? idx : 0;
});

const isLastStep = computed(() => activeIndex.value === stepDefs.value.length - 1);

const isCurrentStepValid = computed(() => {
  const name = route.name;
  if (name === "newActuacion-datos") return !!wizard.datosLegales?.valid;
  if (name === "newActuacion-fecha") return !!wizard.fecha?.valid;
  if (name === "newActuacion-afectados") return !!wizard.afectados?.valid;
  if (name === "newActuacion-vinculados") return true;
  if (name === "newActuacion-efectos") return true;
  if (name === "newActuacion-personal-interviniente") return !!wizard.intervinientes?.valid;
  if (name === "newActuacion-afectacion") return !!wizard.afectaciones?.valid;
  // Para pasos aún no integrados al wizard, permitir avanzar
  return true;
});

const currentStepLabel = computed(() => {
  const idx = activeIndex.value;
  return stepDefs.value[idx]?.label || "";
});

onMounted(() => {
  stepsRef.value.$el.querySelectorAll(".p-menuitem-link").forEach((el) => (el.tabIndex = -1));

  // Si no hay datos en el wizard, es la primera vez que entramos
  // Limpiar stores que puedan tener datos de otras vistas
  if (!wizard.efectos?.data?.effectList || wizard.efectos.data.effectList.length === 0) {
    effectStore.resetForm();
    effectStore.startView();
    if (Array.isArray(effectStore.effectList)) {
      effectStore.effectList.splice(0, effectStore.effectList.length);
    }
  }

  if (!wizard.afectados?.data?.affectedList || wizard.afectados?.data?.affectedList.length === 0) {
    affectedStore.resetForm();
    affectedStore.validateForm();
    affectedStore.startView();
    if (Array.isArray(affectedStore.affectedList)) {
      affectedStore.affectedList.splice(0, affectedStore.affectedList.length);
    }
  }

  if (
    !wizard.vinculados?.data?.vinculatedList ||
    wizard.vinculados?.data?.vinculatedList.length === 0
  ) {
    vinculatedStore.resetForm();
    vinculatedStore.validateForm();
    vinculatedStore.startView();
    if (Array.isArray(vinculatedStore.vinculatedList)) {
      vinculatedStore.vinculatedList.splice(0, vinculatedStore.vinculatedList.length);
    }
  }

  if (
    !wizard.intervinientes?.data?.intervinientesList ||
    wizard.intervinientes.data.intervinientesList.length === 0
  ) {
    intervinienteStore.resetForm();
    intervinienteStore.startView();
    if (Array.isArray(intervinienteStore.intervinientesList)) {
      intervinienteStore.intervinientesList.splice(0, intervinienteStore.intervinientesList.length);
    }
  }

  if (
    !wizard.afectaciones?.data?.afectacionesPatrimoniales ||
    wizard.afectaciones?.data?.afectacionesPatrimoniales.length === 0
  ) {
    afectacionStore.resetForm();
    afectacionStore.validateForm();
    afectacionStore.startView();
    if (Array.isArray(afectacionStore.afectacionesPatrimoniales)) {
      afectacionStore.afectacionesPatrimoniales.splice(
        0,
        afectacionStore.afectacionesPatrimoniales.length
      );
    }
  }
});

watch(
  [flowKey, () => route.name],
  () => {
    const defs = stepDefs.value || [];
    if (!defs.length) return;
    const belongs = defs.some((s) => s.name === route.name);
    if (!belongs) {
      router.replace({ path: defs[0].path, query: route.query });
    }
  },
  { immediate: true }
);

const next = () => {
  if (!isCurrentStepValid.value) return;
  const idx = activeIndex.value;
  const defs = stepDefs.value;
  if (idx < defs.length - 1) {
    router.push({ path: defs[idx + 1].path, query: route.query });
  }
};

const back = () => {
  const idx = activeIndex.value;
  const defs = stepDefs.value;
  if (idx > 0) {
    router.push({ path: defs[idx - 1].path, query: route.query });
  }
};

const saveData = async () => {
  try {
    await currentStore.value.handleSaveLocally();
  } catch (error) {
    console.error(`Error al guardar ${currentStepLabel}: ${error}`);
  } finally {
    showSuccessModal();
    await currentStore.value.selectFirst();
  }
};

// Submit final de la actuación. Sincroniza el acta desde el editor (si el step
// activo lo expone), dispara el POST y ante éxito abre el modal de confirmación
// que luego redirige al detalle de la actuación creada.
const handleSaveActuacion = async () => {
  try {
    if (typeof activeStepRef.value?.syncEditorContent === "function") {
      activeStepRef.value.syncEditorContent();
    }

    const res = await submit();

    if (res?.ok && res?.id) {
      await resetSteps();
      showCreatedSuccessModal(res.id);
    }
  } catch (error) {
    console.error("Error al crear la actuación", error);
  }
};

const clearCreatedRedirect = () => {
  createdRedirectCountdown.value = null;

  if (createdRedirectTimeout) {
    clearTimeout(createdRedirectTimeout);
    createdRedirectTimeout = null;
  }

  if (createdRedirectInterval) {
    clearInterval(createdRedirectInterval);
    createdRedirectInterval = null;
  }
};

const redirectToCreatedDetail = (nuevoId) => {
  clearCreatedRedirect();
  confirmationModalRef.value?.close();
  skipLeaveGuard.value = true;
  router.push({ path: `/actuaciones/view/${nuevoId}` }).catch(() => {});
};

const startCreatedRedirect = (nuevoId) => {
  clearCreatedRedirect();
  createdRedirectCountdown.value = 3;

  createdRedirectInterval = window.setInterval(() => {
    if (createdRedirectCountdown.value > 1) {
      createdRedirectCountdown.value -= 1;
    }
  }, 1000);

  createdRedirectTimeout = window.setTimeout(() => {
    redirectToCreatedDetail(nuevoId);
  }, 3000);
};

const showCreatedSuccessModal = (nuevoId) => {
  modalConfig.value = {
    title: "¡Actuación creada con éxito!",
    message: "Serás redirigido al detalle de la actuación.",
    modalType: "success",
    showSecondaryButton: false,
    closable: false,
    buttonLabels: ["Ir al detalle"],
    onConfirm: () => redirectToCreatedDetail(nuevoId),
    onCancel: () => redirectToCreatedDetail(nuevoId),
  };

  startCreatedRedirect(nuevoId);
  confirmationModalRef.value?.open();
};

const resetSteps = async () => {
  try {
    // Reset wizard
    wizard.setDatosLegalesData({});
    wizard.setDatosLegalesValid(false);
    wizard.setStepData("afectados", {});
    wizard.setStepValid("afectados", false);
    wizard.setStepData("vinculados", {});
    wizard.setStepValid("vinculados", false);
    wizard.setStepData("fecha", {});
    wizard.setStepValid("fecha", false);
    wizard.setStepData("efectos", {});
    wizard.setStepValid("efectos", false);
    wizard.setStepData("afectaciones", {});
    wizard.setStepValid("afectaciones", false);

    // Reset stores internos conocidos (no modificamos su implementación)
    legalStore.reset?.();
    legalStore.startEdit?.();
    legalStore.initializeFormData();

    // await preloadCatalogs(flowKey.value);
    //comentado por que bloquea el reset al fallar con errores silenciosos no manejados- santi

    vinculatedStore.resetForm();
    vinculatedStore.validateForm();
    vinculatedStore.startView();
    if (Array.isArray(vinculatedStore.vinculatedList)) {
      vinculatedStore.vinculatedList.splice(0, vinculatedStore.vinculatedList.length);
    }

    affectedStore.resetForm();
    affectedStore.validateForm();
    affectedStore.startView();
    if (Array.isArray(affectedStore.affectedList)) {
      affectedStore.affectedList.splice(0, affectedStore.affectedList.length);
    }

    intervinienteStore.resetForm();
    intervinienteStore.validateForm();
    intervinienteStore.startView();
    if (Array.isArray(intervinienteStore.intervinientesList)) {
      intervinienteStore.intervinientesList.splice(0, intervinienteStore.intervinientesList.length);
    }

    effectStore.resetForm();
    effectStore.startView();
    if (Array.isArray(effectStore.effectList)) {
      effectStore.effectList.splice(0, effectStore.effectList.length);
    }

    afectacionStore.resetForm();
    afectacionStore.startView();
    if (Array.isArray(afectacionStore.afectacionesPatrimoniales)) {
      afectacionStore.afectacionesPatrimoniales.splice(
        0,
        afectacionStore.afectacionesPatrimoniales.length
      );
    }
  } catch {
    console.error("Error en reset");
  }
};

const resetAndStartView = async (store) => {
  store.clear();
  store.resetForm();
  store.validateForm();
  store.startView();

  try {
    await nextTick();
    store.selectFirst?.();
  } catch (e) {
    console.error("Error al ejecutar selectFirst después de nextTick", e);
  }
};

const goFirst = () => {
  const first = stepDefs.value?.[0];
  if (first) router.replace({ path: first.path, query: route.query });
};

const openDeleteModal = () => deleteModal.value?.open();
const onConfirmDelete = () => {
  resetSteps();
  goFirst();
  deleteModal.value?.close();
};
const onCancelDelete = () => deleteModal.value?.close();

/* Modal config */
const modalConfig = ref({
  title: "",
  message: "",
  modalType: "",
  showSecondaryButton: true,
  buttonLabels: [],
  onConfirm: () => {},
  onCancel: () => {},
});

const showCancelModal = () => {
  modalConfig.value = {
    title: "Cancelar carga de datos",
    message:
      "Si cancelas ahora, la información ingresada se perderá y no podrá recuperarse. ¿Deseas continuar con la cancelación?",
    modalType: "warning",
    showSecondaryButton: true,
    onConfirm: () => {
      resetAndStartView(currentStore.value);
      confirmationModalRef.value?.close();
    },
    onCancel: () => {
      confirmationModalRef.value?.close();
    },
  };

  confirmationModalRef.value?.open();
};

const showSuccessModal = () => {
  modalConfig.value = {
    title: "¡Los datos se guardaron con éxito!",
    modalType: "success",
    showSecondaryButton: false,
    onConfirm: () => {
      currentStore.value.startView();
      confirmationModalRef.value?.close();
      currentStore.value.selectFirst();
    },
    onCancel: () => {
      confirmationModalRef.value?.close();
    },
  };

  confirmationModalRef.value?.open();
};

const showConfirmModal = () => {
  modalConfig.value = {
    title: "Cancelar carga de datos",
    message:
      "Si cancelas ahora, la información ingresada se perderá y no podrá recuperarse. ¿Deseas continuar con la cancelación?",
    modalType: "warning",
    showSecondaryButton: true,
    onConfirm: () => {
      if (pendingNext.value) {
        pendingNext.value();
        pendingNext.value = null;
      }
      resetSteps();
      confirmationModalRef.value?.close();
    },
    onCancel: () => {
      if (pendingNext.value) {
        pendingNext.value(false);
        pendingNext.value = null;
      }
      confirmationModalRef.value?.close();
    },
  };

  confirmationModalRef.value?.open();
};

const showConfirmDelete = () => {
  const config = currentStepConfig.value;
  if (!config) return;

  modalConfig.value = {
    title: `Eliminar ${config.label}`,
    message: `¿Está seguro de eliminar este ${config.label}? Esta acción no se puede deshacer.`,
    modalType: "warning",
    showSecondaryButton: true,
    buttonLabels: ["Eliminar", "Cancelar"],
    onConfirm: async () => {
      modalConfig.value.buttonLabels = ["Eliminando...", "Cancelar"];

      if (!config.delete || !currentStore.value) return;

      try {
        currentStore.value.isLoading = true;
        await config.delete(true);
        confirmationModalRef.value?.close();
        showSuccessModal();
      } catch (err) {
        console.error("Error al eliminar:", err);
        showFailureModal();
      } finally {
        if (currentStore.value) {
          currentStore.value.isLoading = false;
          currentStore.value.clear();
        }
      }
    },
    onCancel: () => confirmationModalRef.value?.close(),
  };

  confirmationModalRef.value?.open();
};

const showFailureModal = () => {
  const label = currentStepConfig.value?.label ?? "elemento";

  modalConfig.value = {
    title: `Error al eliminar ${label}`,
    message: `No se pudo eliminar el ${label}. Intente nuevamente.`,
    modalType: "warning",
    showSecondaryButton: false,
    onConfirm: () => confirmationModalRef.value?.close(),
  };

  confirmationModalRef.value?.open();
};

const pendingNext = ref(null);

onBeforeRouteLeave((to, from, next) => {
  // Cuando venimos de un submit exitoso saltamos el modal de cancelar carga:
  // o bien se fijó la bandera, o el destino es el detalle de la actuación.
  if (skipLeaveGuard.value || (to.path && to.path.startsWith("/actuaciones/view/"))) {
    skipLeaveGuard.value = false;
    next();
    return;
  }
  pendingNext.value = next;
  showConfirmModal();
});
onBeforeRouteUpdate((to, from, next) => {
  const sameForm = to.query.type === from.query.type;

  if (sameForm) {
    next();
    return;
  }

  pendingNext.value = next;
  showConfirmModal();
});

onBeforeUnmount(() => {
  clearCreatedRedirect();
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100..700;1,100..700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");

.title {
  font-family: "IBM Plex Sans";
  font-weight: 700;
  font-style: Bold;
  font-size: 28px;
  line-height: 32px;
  vertical-align: middle;
  color: black;
  margin: 0;
}
.flow-subtitle {
  color: black;
  font-family: "IBM Plex Sans";
  font-weight: 700;
  font-style: Bold;
  font-size: 24px;
  line-height: 100%;
  vertical-align: middle;
  margin: 0;
}

.main-card {
  width: 100%;
  display: flex;
  justify-content: center;
}

.stepName {
  font-family: "IBM Plex Sans";
  font-weight: 700;
  font-style: Bold;
  font-size: 24px;
  line-height: 100%;
  vertical-align: middle;
}

.button {
  min-width: 130px;
  min-height: 43px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  border: none;
  border-radius: 6px;
}

.created-redirect-button {
  min-width: 130px;
  min-height: 43px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  border-radius: 6px;
  transition: opacity 0.1s ease-in-out;
}

.created-redirect-button:hover {
  opacity: 0.7;
}

.modal-footer-note {
  margin-top: 0.75rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.primaryButton {
  color: white;
  background-color: #0b5195;
}

.primaryButton:hover {
  background-color: #1366b0;
}

.secondaryButton {
  background-color: white;
  color: #0b5195;
  border: 1px #0b5195 solid;
}

.secondaryButton:hover {
  background-color: rgba(255, 255, 255, 0.267);
  color: #1366b0;
}

.deleteButton {
  color: #d20f39;
  background-color: white;
  min-height: 43px;
  border: 1px #d20f39 solid;
  border-radius: 6px;
  min-width: 145px;
  display: flex;
  justify-content: center;
  font-family: Inter;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
}

.deleteButton:hover {
  background-color: #d20f39b9;
}

/* Paso activo */

/* Activo: soporta tanto p-highlight (li) como clases del router en el link */
:deep(.p-steps) .p-steps-item.p-highlight .p-steps-number,
:deep(.p-steps) .p-menuitem-link.router-link-exact-active .p-steps-number,
:deep(.p-steps) .p-menuitem-link.router-link-active .p-steps-number {
  background-color: #0b5195 !important;
  color: #ffffff !important;
  border-color: #0b5195 !important;
}

:deep(.p-steps) .p-steps-item.p-highlight .p-steps-title,
:deep(.p-steps) .p-menuitem-link.router-link-exact-active .p-steps-title,
:deep(.p-steps) .p-menuitem-link.router-link-active .p-steps-title {
  color: #0b5195 !important;
  font-weight: 600 !important;
}

/* Pasos inactivos */
/* Inactivo: ni p-highlight ni router-link activo */
:deep(.p-steps)
  .p-steps-item:not(.p-highlight)
  .p-menuitem-link:not(.router-link-exact-active):not(.router-link-active)
  .p-steps-number {
  background-color: #ffffff !important;
  color: black !important;
  border: 2px solid #0b5195 !important;
}

:deep(.p-steps)
  .p-steps-item:not(.p-highlight)
  .p-menuitem-link:not(.router-link-exact-active):not(.router-link-active)
  .p-steps-title {
  color: #6c757d !important;
}

.p-menuitem-link {
  font-family: "Inter";
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: center;
}

.p-steps .p-steps-number {
  border-radius: 50% !important;
  width: 2rem !important;
  height: 2rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.steps-readonly .p-menuitem-link) {
  pointer-events: none;
  cursor: default;
}

/* Step marcado con error del backend (422) */
:deep(.p-steps) .p-steps-item.wizard-step-error .p-steps-number {
  background-color: #d20f39 !important;
  color: #ffffff !important;
  border-color: #d20f39 !important;
}

:deep(.p-steps) .p-steps-item.wizard-step-error .p-steps-title {
  color: #d20f39 !important;
  font-weight: 600 !important;
}
</style>
